import {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import * as AssignmentsAPI from 'src/api/Assignments';
import { useResourceStore } from 'src/hooks/store/resources';
import { Assignment } from 'src/types/models';
import { useDocument } from './document';

export function useAssignment(assignmentId: string | undefined) {
  const assignment = useResourceStore((state) => (
    assignmentId ? state.assignmentById[assignmentId] : undefined));
  const storeAssignment = useResourceStore((state) => state.setAssignment);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const baseDocument = useDocument(assignment?.baseDocument?.id);

  useEffect(() => {
    if (assignmentId && assignment === undefined && !isLoading && !isError) {
      // The assignment has not been loaded.
      // Fetch the Assignment from the server and map to client-side naming convention.
      setIsLoading(true);
      AssignmentsAPI.getById(assignmentId).then((response) => {
        const { data } = response.data;

        // Put the assignment into the global data store.
        storeAssignment({
          id: data.id,
          name: data.name,
          classrooms: data.classrooms,
          students: data.students,
          baseDocument: data['base-document'] ? {
            id: data['base-document'].id,
          } : null,
          teacher: {
            id: data.teacher.id,
            name: data.teacher.name,
            surname: data.teacher.surname,
          },
          createdAt: new Date(data.created_at),
          updatedAt: new Date(data.updated_at),

          // Assignment instructions are no longer stored as Notes.
          // Update the representation to instructions.
          // The note will be ignored if `instructions` is set.
          instructions: (
            data.instructions
            || (data.notes?.length ? data.notes[0].text : '')
          ),
          notes: [],

          // These fields are specific to students:
          submission: data.submission ? {
            id: data.submission?.id,
            documentId: data.submission?.document_id,
            status: data.submission?.status,
          } : null,
        });
      }).catch(() => {
        // Pattern in this repository is to use null to represent a loading error.
        // Add specific error handling here if it should be displayed to the user.
        setIsError(true);
      }).finally(() => {
        setIsLoading(false);
      });
    }
  }, [assignmentId, assignment, storeAssignment]);

  const update = useCallback(async (fields: Partial<Assignment>) => {
    // An assignment must have an ID, and it cannot be changed by this function.
    if (!assignmentId || !assignment) {
      return;
    }

    if (fields.id !== undefined && fields.id !== assignmentId) {
      throw new Error(
        `Assignment ID cannot be changed. Tried changing from ${assignmentId} to ${fields.id}`,
      );
    }

    // Immediately update to the global store.
    const newAssignment = {
      ...assignment,
      ...fields,
      id: assignmentId,
    };
    storeAssignment(newAssignment);

    // Post to the server.
    await AssignmentsAPI.update(assignmentId, {
      name: newAssignment.name,
      document: newAssignment.baseDocument?.id ? { id: newAssignment.baseDocument.id } : undefined,
      instructions: newAssignment.instructions,
    });
  }, []);

  const enrichedAssignment = useMemo(() => {
    if (isLoading) {
      return undefined;
    }

    if (isError) {
      return null;
    }

    // Enrich the assignment with the base document. This will keep the
    // base document in sync with the store.
    if (assignment) {
      return {
        ...assignment,
        baseDocument: baseDocument || assignment?.baseDocument,
      };
    }

    return undefined;
  }, [assignment, baseDocument, isLoading, isError]);

  return [enrichedAssignment, update] as const;
}
